import React from "react";
import { Check } from "lucide-react";
import SeoLandingLayout from "@/components/SeoLandingLayout";
import { LuxeOverline } from "@/components/LuxeOverline";
import { IMAGES, LODGES } from "@/lib/site";

const FAQS = [
  {
    q: "Is glamping at Walnut Lakes a tent or a lodge?",
    a: "Proper lodges — real log cabins with solid walls, heating, a fully-fitted kitchen and a flat-screen TV. All the romance of glamping by the water, none of the compromise.",
  },
  {
    q: "What's included in a glamping stay?",
    a: "Bedding, towels and linen are all included, along with a private deck overlooking the lake and free fishing on both private carp lakes (one complimentary rod per person per stay).",
  },
  {
    q: "How much does glamping cost per night?",
    a: "Heron Lodge starts from £90 per night, Kingfisher from £100 and our flagship Alpine from £120, based on two guests. Extra guests (up to four) are £15 each per stay.",
  },
  {
    q: "Can I bring my dog glamping?",
    a: "Yes — all three lodges are dog-friendly. Up to two dogs are welcome (£30 for one, £45 for two per stay) and there's a half-mile enclosed dog walk on-site.",
  },
  {
    q: "Is there a minimum stay?",
    a: "Two nights across the season, which runs mid-March to the end of November. Check-in is from 2:00 PM and check-out by 11:00 AM.",
  },
  {
    q: "Is glamping at Walnut Lakes suitable for couples?",
    a: "It's designed for exactly that — Walnut Lakes is strictly adults-only (15+), so your glamping break comes with guaranteed peace, quiet and big fenland skies.",
  },
];

const LUXE = [
  "Private deck over the water — morning coffee with the herons",
  "Fully-fitted kitchen & flat-screen TV — glamping without the rough edges",
  "Bedding, towels & linen all included",
  "Free fishing on two private carp lakes",
  "Adults only (15+) — guaranteed peace and quiet",
  "Dog friendly — up to two pups welcome",
];

const GlampingLincolnshire = () => (
  <SeoLandingLayout
    seo={{
      title: "Glamping in Lincolnshire · Lakeside Lodges with Private Decks near Boston",
      description:
        "Glamping in Lincolnshire without the compromise — three adults-only lakeside log lodges at Walnut Lakes near Boston & Spalding. Kitchen, TV, private deck, free fishing. From £90/night.",
      path: "/glamping-lincolnshire",
    }}
    overline="Glamping in Lincolnshire"
    titlePre="Glamping in Lincolnshire,"
    titleEm="with proper walls."
    intro="If you love the idea of glamping but not the drafty tent, Walnut Lakes is your answer: three real log lodges on the water's edge of two private fishing lakes, in ten acres of adults-only Lincolnshire fenland between Boston and Spalding. Nature outside the window, comfort inside it."
    heroImage={IMAGES.lodgeFromOutside}
    heroAlt="Luxury glamping lodge beside the lake at Walnut Lakes, Lincolnshire"
    faqs={FAQS}
  >
    {/* The lodges */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Three lodges</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
        Choose your waterside hideaway
      </h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        {LODGES.map((l) => (
          <div key={l.id} className="rounded-2xl overflow-hidden bg-[#14100F] border border-white/10">
            <img src={l.image} alt={`${l.name} — glamping lodge in Lincolnshire`} className="w-full h-52 object-cover" loading="lazy" />
            <div className="p-6">
              <h3 className="font-serif text-xl text-[#F5EFE6]">{l.name}</h3>
              <p className="text-xs uppercase tracking-[0.2em] text-[#E4B2A8] mt-1">{l.tag}</p>
              <p className="mt-4 text-sm text-[#D8CDBA]">
                From <span className="text-[#F5EFE6] font-semibold">£{l.price}</span> / night · based on 2 guests
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Comfort list */}
    <section className="bg-[#14100F] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <LuxeOverline>Glamping, upgraded</LuxeOverline>
          <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
            All of the outdoors. None of the roughing it.
          </h2>
          <ul className="mt-8 space-y-3">
            {LUXE.map((i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
                <Check size={16} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {i}
              </li>
            ))}
          </ul>
        </div>
        <img
          src={IMAGES.lodgeCabin}
          alt="Interior comfort of a glamping lodge at Walnut Lakes Holiday Park, Lincolnshire"
          className="w-full h-[340px] object-cover rounded-2xl"
          loading="lazy"
        />
      </div>
    </section>

    {/* Location context */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>The setting</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4 max-w-2xl">
        Big-sky glamping between Boston and the coast
      </h2>
      <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-[#D8CDBA]">
        Just off the A16/A17 near Sutterton, the park is minutes from Boston's historic Stump
        and market town of Spalding, with RSPB Frampton Marsh ten minutes by car and the
        Lincolnshire coast within easy reach. Village shops, pubs and a farm shop are a
        15-minute walk — close enough for convenience, far enough for stars.
      </p>
    </section>
  </SeoLandingLayout>
);

export default GlampingLincolnshire;
